import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { AuthSyncService } from './common/services/auth-sync/auth-sync.service';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { META_REDUCERS, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { userReducer } from './common/state/user/user.reducers';
import { UserEffects } from './common/state/user/user.effects';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { errorInterceptor, tokenInterceptor } from './common/interceptors/token.interceptor';
import { AuditEffects } from './common/state/audit/audit.effects';
import { DeviceEffects } from './common/state/devices/device.effects';
import { deviceReducer } from './common/state/devices/device.reducers';
import { ReportEffects } from './common/state/report/report.effects';
import { reportReducer } from './common/state/report/report.reducer';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { siteReducer } from './common/state/site/site.reducers';
import { SiteEffects } from './common/state/site/site.effects';
import { SensorEffects } from './common/state/camera/camera.effects';
import { sensorReducer } from './common/state/camera/reducer';
import { accountReducer } from './account-management/store/customer.reducer';
import { AccountEffects } from './account-management/store/customer.effects';
import { roleLookupReducer } from './common/state/roles/roles.reducer';
import { RoleLookupEffects } from './common/state/roles/roles.effects';
import { licenseReducer } from './common/state/license/license.reducers';
import { LicenseEffects } from './common/state/license/license.effects';
import { alertReducer } from './common/state/alert/alert.reducer';
import { AlertEffects } from './common/state/alert/alert.effects';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { NotificationEffects } from './common/state/notification/notification.effects';
import { notificationReducer } from './common/state/notification/notification.reducer';
import { recordingsReducer } from './recordings/store/recordings.reducer';
import { RecordingsEffects } from './recordings/store/recordings.effects';
import { integrationsReducer } from './common/state/integrations/notification.reducer';
import { IntegrationsEffects } from './common/state/integrations/integrations.effects';
import { settingsReducer } from './common/state/settings/settings.reducer';
import { healthFilterReducer } from './common/state/health-filter/health-filter.reducer';
import { ruleReducer } from './common/state/rule/rule.reducer';
import { RuleEffects } from './common/state/rule/rule.effects';
import { featureManagementReducer } from './compute-credit-management/store/feature-management.reducer';
import { FeatureManagementEffects } from './compute-credit-management/store/feature-management.effect';
import { featureFamilyReducer } from './feature-family-management/store/feature-family.reducer';
import { FeatureFamilyEffects } from './feature-family-management/store/feature-family.effects';
import { tagReducer } from './common/state/tag/tag.reducer';
import { TagEffects } from './common/state/tag/tag.effects';
import { getLogRocketMetaReducer } from './common/services/log-rocket/log-rocket.helpers';
import { licenseV2Reducer } from './subscriptions-plus/store/subscriptions-plus.reducer';
import { LicenseV2Effects } from './subscriptions-plus/store/subscriptions-plus.effects';
import { alertReportTemplateReducer } from './alert-report-templates/store/alert-report-template.reducer';
import { AlertReportTemplateEffects } from './alert-report-templates/store/alert-report-template.effects';
import { patrolReducer } from './common/state/patrol/patrol.reducer';
import { PatrolEffects } from './common/state/patrol/patrol.effects';
import { spotlyteReducer } from './common/state/spotlyte/spotlyte.reducer';
import { SpotlyteEffects } from './common/state/spotlyte/spotlyte.effects';
import { audioDeviceReducer } from './common/state/audio-device/audio-device.reducer';
import { AudioDeviceEffects } from './common/state/audio-device/audio-device.effects';
import { aggregateReducer } from './alert-dashboard/store/aggregate.reducer';
import { AggregateEffects } from './alert-dashboard/store/aggregate.effects';
import { registryReducer } from './common/state/registry/registry.reducer';
import { RegistryEffects } from './common/state/registry/registry.effects';
import { HealthEffects } from './common/state/health/health.effects';
import { ruleScheduleReducer } from './common/state/rule-schedule/rule-schedule.reducer';
import { RuleScheduleEffects } from './common/state/rule-schedule/rule-schedule.effects';


const REDUCERS = {
  accounts: accountReducer,
  alerts: alertReducer,
  devices: deviceReducer,
  integrations: integrationsReducer,
  licenses: licenseReducer,
  notifications: notificationReducer,
  recordings: recordingsReducer,
  reports: reportReducer,
  roles: roleLookupReducer,
  rules: ruleReducer,
  sensors: sensorReducer,
  sites: siteReducer,
  user: userReducer,
  settings: settingsReducer,
  healthFilter: healthFilterReducer,
  tags: tagReducer,
  // TODO: update name and merge credit system related items into one state slice
  features: featureManagementReducer,
  featureFamilies: featureFamilyReducer,
  subscriptionsPlus: licenseV2Reducer,
  alertReportTemplates: alertReportTemplateReducer,
  patrol: patrolReducer,
  ruleSchedules: ruleScheduleReducer,
  spotlyte: spotlyteReducer,
  audioDevices: audioDeviceReducer,
  aggregate: aggregateReducer,
  registries: registryReducer,
}

const EFFECTS = [
  UserEffects,
  AuditEffects,
  DeviceEffects,
  SiteEffects,
  SensorEffects,
  ReportEffects,
  AccountEffects,
  RoleLookupEffects,
  LicenseEffects,
  AlertEffects,
  NotificationEffects,
  RecordingsEffects,
  IntegrationsEffects,
  RuleEffects,
  FeatureManagementEffects,
  FeatureFamilyEffects,
  TagEffects,
  LicenseV2Effects,
  AlertReportTemplateEffects,
  PatrolEffects,
  RuleScheduleEffects,
  SpotlyteEffects,
  AudioDeviceEffects,
  AggregateEffects,
  RegistryEffects,
  HealthEffects,
]

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    provideStore(REDUCERS),
    provideEffects(EFFECTS),
    provideRouterStore(),
    provideHttpClient(withXhr(), withInterceptors([tokenInterceptor, errorInterceptor])),
    provideAppInitializer(() => inject(AuthSyncService).start()),
    // autoPause: inert unless the panel is open - per-dispatch state serialization
    // was the GB-scale allocator. The blocklist keeps base64-carrying actions out
    // even when the panel is recording.
    ...(isDevMode() ? [provideStoreDevtools({
      maxAge: 25,
      logOnly: false,
      name: 'Lytehouse',
      autoPause: true,
      actionsBlocklist: ['[Camera] primeSnapshotFromLivestream', '[Camera] overrideSnapshot'],
    })] : []),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: 'dialog', restoreFocus: true }
    },
    {
      provide: META_REDUCERS,
      deps: [],
      useFactory: getLogRocketMetaReducer,
      multi: true,
    },
  ]
};
